import React, { useEffect, useMemo, useState } from "react";
import { Eye, Trash2, CheckCheck, Search, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, money, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const STATUS = {
  pendiente: ["Pendiente de pago", "bg-gray-100 text-gray-700"],
  abonado: ["Abonado (parcial)", "bg-amber-100 text-amber-800"],
  pagado: ["Pagado 100%", "bg-emerald-100 text-emerald-800"],
  expirado: ["Expirado", "bg-red-100 text-red-700"],
  cancelado: ["Cancelado", "bg-red-100 text-red-700"],
  completado: ["Tour completado", "bg-sky-100 text-sky-800"],
};

export const StatusBadge = ({ status }) => {
  const [l, c] = STATUS[status] || [status, "bg-gray-100 text-gray-700"];
  return <span className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${c}`}>{l}</span>;
};

export default function BookingsAdmin({ onChange }) {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);
  const [del, setDel] = useState(null);

  const load = () => api.get("/admin/bookings").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (list || []).filter((b) =>
    (filter === "all" || b.status === filter) &&
    (!q || `${b.code} ${b.customer.name} ${b.customer.email}`.toLowerCase().includes(q.toLowerCase()))), [list, filter, q]);

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, body);
      setList((l) => l.map((b) => (b.id === id ? data : b))); setView(data); onChange(); toast.success("Reserva actualizada");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const downloadInvoice = async (b) => {
    try {
      const res = await api.get(`/admin/bookings/${b.id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Factura-${b.invoice_number || b.code}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      load();
    } catch { toast.error("La factura solo está disponible para pagos confirmados"); }
  };
  const remove = async () => {
    await api.delete(`/admin/bookings/${del.id}`); setDel(null); setView(null); load(); onChange(); toast.success("Eliminada");
  };

  if (!list) return <Loader2 className="w-6 h-6 animate-spin brand-text" />;

  return (
    <div className="space-y-4" data-testid="admin-bookings">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input className="pl-9 bg-white" placeholder="Buscar por código, nombre o correo" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bookings-search-input" /></div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="sm:w-56 bg-white" data-testid="bookings-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr>
            <th className="p-3 font-medium">Código</th><th className="p-3 font-medium">Cliente</th><th className="p-3 font-medium">Excursiones</th>
            <th className="p-3 font-medium">%</th><th className="p-3 font-medium">Pagado / Total</th><th className="p-3 font-medium">Estado</th><th className="p-3 font-medium">Fecha</th><th className="p-3"></th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-gray-500">Sin registros</td></tr>}
            {filtered.map((b) => (
              <tr key={b.id} className="hover:bg-gray-50" data-testid="booking-row">
                <td className="p-3 font-mono text-xs">{b.code}</td>
                <td className="p-3"><div className="font-medium">{b.customer.name}</div><div className="text-xs text-gray-500">{b.customer.email}</div></td>
                <td className="p-3 max-w-[220px] truncate">{b.items.map((i) => i.title).join(", ")}</td>
                <td className="p-3">{b.payment_percent}%</td>
                <td className="p-3 whitespace-nowrap"><span className="font-semibold">{money(b.amount_paid)}</span> / {money(b.total)}</td>
                <td className="p-3"><StatusBadge status={b.status} /></td>
                <td className="p-3 text-xs text-gray-500 whitespace-nowrap">{new Date(b.created_at).toLocaleString("es-DO")}</td>
                <td className="p-3"><button onClick={() => setView(b)} className="p-2 rounded hover:bg-gray-100" aria-label="Ver" data-testid="booking-view-btn"><Eye className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {view && (<>
            <DialogHeader><DialogTitle>Reserva {view.code}{view.invoice_number && <span className="ml-2 text-sm font-normal text-gray-500">· {view.invoice_number}</span>}</DialogTitle></DialogHeader>
            <div className="space-y-4 text-sm">
              <StatusBadge status={view.status} />
              <div className="rounded-lg bg-gray-50 p-3 space-y-1">
                <div><b>{view.customer.name}</b></div><div>{view.customer.email} · {view.customer.phone}</div>
                <div>Hotel: {view.customer.hotel || "-"}</div>{view.customer.notes && <div>Notas: {view.customer.notes}</div>}
              </div>
              <div className="divide-y border rounded-lg">
                {view.items.map((i, k) => <div key={k} className="p-3 flex justify-between gap-2"><span>{i.title}<span className="block text-xs text-gray-500">{i.date} · {i.adults} adultos, {i.children} niños</span></span><span>{money(i.subtotal)}</span></div>)}
              </div>
              <div className="space-y-1">
                {view.coupon_code && <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{money(view.subtotal)}</span></div>}
                {view.coupon_code && <div className="flex justify-between text-emerald-700"><span>Cupón {view.coupon_code}</span><span>-{money(view.discount)}</span></div>}
                <div className="flex justify-between"><span>Total</span><b>{money(view.total)}</b></div>
                <div className="flex justify-between text-emerald-700"><span>Pagado ({view.payment_percent}%)</span><b>{money(view.amount_paid)}</b></div>
                <div className="flex justify-between text-amber-700"><span>Saldo pendiente</span><b>{money(view.balance_due)}</b></div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Cambiar estado</label>
                <Select value={view.status} onValueChange={(v) => patch(view.id, { status: v })}>
                  <SelectTrigger className="mt-1" data-testid="booking-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {view.payment_status === "paid" && (
                  <button onClick={() => downloadInvoice(view)} className="btn-accent px-4 py-2 rounded-md flex items-center gap-2 font-medium" data-testid="admin-download-invoice-btn"><FileDown className="w-4 h-4" />Factura PDF{view.invoice_number ? ` ${view.invoice_number}` : ""}</button>
                )}
                {view.balance_due > 0 && view.payment_status === "paid" && (
                  <button onClick={() => patch(view.id, { mark_balance_paid: true })} className="btn-brand px-4 py-2 rounded-md flex items-center gap-2" data-testid="mark-balance-paid-btn"><CheckCheck className="w-4 h-4" />Marcar saldo cobrado</button>
                )}
                <button onClick={() => setDel(view)} className="px-4 py-2 rounded-md border border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-2" data-testid="booking-delete-btn"><Trash2 className="w-4 h-4" />Eliminar</button>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar esta reserva?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer. No reembolsa el pago en Stripe.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
